import {
  Activity,
  // Award, // TEMP-HIDDEN: CRM Commission rail item (this version)
  BarChart2,
  BarChart3,
  Briefcase,
  Building2,
  Calculator,
  Calendar,
  CalendarClock,
  CalendarDays,
  CalendarMinus2,
  CircleDollarSign,
  ClipboardCheck,
  ClipboardList,
  Phone,
  Clock,
  Contact,
  DoorOpen,
  FileEdit,
  FileCheck,
  FileText,
  FolderKanban,
  Gauge,
  HandCoins,
  Handshake,
  Headphones,
  Home,
  IdCard,
  Layers,
  LayoutDashboard,
  LayoutGrid,
  // LineChart, // TEMP-HIDDEN: CRM Reports rail item (this version)
  Link2,
  ListTodo,
  Mic,
  Settings,
  Shield,
  Inbox,
  Network,
  RotateCcw,
  ScrollText,
  Target,
  Ticket,
  Timer,
  UserCheck,
  Users,
  UsersRound,
  Wallet,
} from 'lucide-react';
import type { AppDefinition, PermissionCode } from '@clarix/shared-types';
import { SETTINGS_RAIL_PERMISSIONS } from './settings-tabs';
import { projectsSidebar } from './sidebar/projectsPanels';
import { workspaceSidebar } from './sidebar/workspacePanels';
import { supportSidebar } from './sidebar/supportPanels';
import { crmSidebar } from './sidebar/crmPanels';
import { calendarSidebar } from './sidebar/calendarPanels';
import { hrmsSidebar } from './sidebar/hrmsPanels';
import { CALENDAR_SHELL_PERM } from './calendar-permissions';
import { HRMS_SHELL_PERM } from './hrms-permissions';

// Static app metadata lives in the shell, NOT in the remotes. The AppSwitcher
// and route guards must render before any remote loads, so id/slug/name/icon/
// favicon/permissions/railItems are declared here. The ONLY thing that crosses
// the host/remote boundary at runtime is `loader: () => import('clarix<App>/App')`,
// resolved by the Module Federation plugin from the version-pinned manifest.
//
// Each remote keeps its own register.ts for its standalone dev harness; the
// shell deliberately does not import it (that would be build-time coupling).

const projectsRegister: AppDefinition = {
  id: 'projects',
  slug: 'projects',
  name: 'Projects',
  shortName: 'PM',
  icon: Briefcase,
  favicon: '/favicons/projects.svg',
  brandColor: '#2563EB',
  permissions: [
    'projects.project.read',
    'projects.project.write',
    'projects.project.delete',
    'projects.team.read',
    'projects.team.write',
    'projects.module.read',
    'projects.module.write',
    'projects.feature.read',
    'projects.feature.write',
    'projects.task.read',
    'projects.task.write',
    'projects.settings.read',
    'projects.settings.write',
  ] as const satisfies readonly PermissionCode[],
  loader: () => import('clarixProjects/App'),
  // Exact replica of clario-frontend SidebarRail `topRailItems` — same icons,
  // labels, order, permission gating, and hover-panel ids. Hrefs are remapped
  // under /projects (contract rule: every app rail href starts with the app
  // slug). Home is the shared AppHome surface (the /projects index), with My
  // Work as its own entry. Panel bodies live in apps/gateway/src/gateway/SidebarPanels.tsx.
  railItems: [
    {
      id: 'projects-home',
      label: 'Home',
      icon: Home,
      href: '/home',
      exact: true,
      moduleCode: 'my_work',
    },
    // Mirrors apps/projects/src/rail.ts — My Work moved off the index when
    // Home became the shared AppHome surface, so it needs its own rail entry.
    { id: 'projects-my-work', label: 'My Work', icon: ListTodo, href: '/projects/my-work' },
    {
      id: 'projects-list',
      moduleCode: 'projects',
      label: 'Projects',
      icon: FolderKanban,
      href: '/projects/list',
      panelId: 'projects',
      // The Projects item owns the whole project subtree — the list page AND
      // every project detail route (`/projects/:id`, opened from the My
      // Projects sidebar). Home is exact (`/projects`), so it still wins the
      // index; a more-specific rail item (timesheet, settings, …) still wins
      // its own `/projects/<section>` via longest-prefix matching.
      matchPaths: ['/projects'],
    },
    {
      id: 'projects-timesheet',
      moduleCode: 'timesheet',
      label: 'Timesheet',
      icon: Clock,
      href: '/projects/my-timesheet',
      permission: 'module.timesheet.view.own',
    },
    {
      id: 'projects-timesheet-review',
      moduleCode: 'timesheet',
      label: 'Review',
      icon: ClipboardCheck,
      href: '/projects/timesheets/review',
      panelId: 'timesheet',
      permission: ['module.timesheet.review.project', 'module.timesheet.review.all'],
      permissionAny: true,
    },
    {
      id: 'projects-reports',
      moduleCode: 'reports',
      label: 'Reports',
      icon: BarChart3,
      href: '/projects/timesheets/reports',
      panelId: 'reports',
      permission: ['module.my_report.view', 'module.report.timesheet.view', 'module.my_team_report.view'],
      permissionAny: true,
    },
    // No Calendar item: Projects meetings moved to the Calendar app (mirrors apps/projects/src/rail.ts).
    // The Spark RAIL ITEM is gone with the board (2026-08). Raising a spark is a
    // top-bar action in every app now, not a destination: the feed, the votes and
    // the triage all moved to the platform console, so there is nothing in a
    // workspace to navigate TO. See @clarix/spark.
    {
      id: 'projects-users',
      moduleCode: 'users',
      label: 'Users',
      icon: UsersRound,
      href: '/projects/users',
      panelId: 'users',
      permission: 'module.users.view.directory',
    },
    {
      // First-class rail icon (not the 9-dot "More" menu) so its panel opens,
      // toggles, and hover-previews exactly like the other rail icons.
      id: 'projects-settings',
      moduleCode: 'settings',
      label: 'Settings',
      icon: Settings,
      href: '/projects/settings',
      panelId: 'settings',
      // Any settings permission opens the icon — not the umbrella key alone.
      // The panel then shows only the rows that key unlocks, so a member
      // granted just "View API access" gets Settings → API access and nothing
      // else. Gating on `module.settings.view` alone hid the icon from exactly
      // the people a per-tab grant is FOR. See SETTINGS_RAIL_PERMISSIONS.
      permission: SETTINGS_RAIL_PERMISSIONS,
      permissionAny: true,
    },
  ],
  // Replica of clario-frontend's `moreMenuItems` (the 9-dot "More" popover),
  // permission-gated identically, with its per-item tile colors. The theme
  // customizer footer entry is still deferred (see the layout design doc).
  moreItems: [
    {
      id: 'projects-activity',
      moduleCode: 'activity',
      label: 'Activity',
      icon: Activity,
      href: '/projects/activity',
      panelId: 'activity',
      permission: ['module.activity.view.own'],
      permissionAny: true,
      tileColor: 'green',
    },
    {
      id: 'projects-health',
      moduleCode: 'platform_health',
      label: 'Health',
      icon: Shield,
      href: '/projects/platform-health',
      panelId: 'health',
      permission: 'module.platformhealth.view',
      tileColor: 'red',
    },
  ],
  // Every Projects hover panel is object-driven — pure JSON rendered by the
  // gateway's sidebar engine. Adding or changing a panel is a data edit in
  // sidebar/projectsPanels.ts, never new React.
  sidebar: projectsSidebar,
};

const workspaceRegister: AppDefinition = {
  id: 'workspace',
  slug: 'workspace',
  name: 'Workspace',
  shortName: 'WS',
  icon: LayoutGrid,
  favicon: '/favicons/workspace.svg',
  brandColor: '#F59E0B',
  // Names what is actually behind the tile (its rail: Home → Members →
  // Settings) instead of the `Open ${name}.` fallback, which told the user
  // nothing. The backend App registry still wins if it supplies a description.
  description: 'Members, roles, workspace lists and organization settings.',
  permissions: ['WORKSPACE.ADMIN.READ' as PermissionCode],
  loader: () => import('clarixWorkspace/App'),
  // Order: Home → Members → Settings. Invites is folded into the Members page
  // (an "Invited" tab + inline invite flow) and Roles & permissions lives inside
  // the Settings panel, so neither has its own rail icon. LLM Usage lives in the
  // 9-dot "More" popover (moreItems). Keep in sync with
  // apps/workspace/src/{rail,register}.ts.
  railItems: [
    // Mirrors apps/workspace/src/rail.ts — the shared AppHome surface, ungated.
    { id: 'workspace-app-home', label: 'Home', icon: Home, href: '/home' },
    // Renders AdminLandingPage; relabelled when the shared home took 'Home'.
    {
      id: 'workspace-home',
      label: 'Overview',
      icon: LayoutDashboard,
      href: '/workspace',
      exact: true,
    },
    {
      // Single people surface (member = employee); legacy routes still highlight it.
      id: 'workspace-members',
      label: 'Employees',
      icon: Contact,
      href: '/workspace/employees',
      matchPaths: ['/workspace/members', '/workspace/org-chart'],
      // Admin CAPABILITY keys, published by the remote's lib/shell-permissions.ts
      // — a Member granted one sees exactly the icons it opens.
      permission: ['manage_members', 'manage_invites'],
      permissionAny: true,
    },
    {
      id: 'workspace-settings',
      label: 'Settings',
      icon: Settings,
      href: '/workspace/configuration',
      // Roles & permissions, the workspace master lists AND the Security pages
      // live inside the Settings panel (no own rail icon), so they highlight
      // Settings instead of falling back to Home on reload / deep link.
      // `/workspace/security` is a prefix match, so it also covers the nested
      // password-policy / ip-allowlist pages.
      // MUST stay in sync with apps/workspace/src/rail.ts — the shell renders
      // the rail from THIS list, not from the remote's copy.
      matchPaths: ['/workspace/permissions', '/workspace/masters', '/workspace/security'],
      panelId: 'workspace-settings',
      permission: ['manage_settings', 'manage_masters', 'manage_apps', 'manage_security'],
      permissionAny: true,
    },
  ],
  moreItems: [
    {
      id: 'workspace-llm-usage',
      label: 'AI Usage',
      icon: CircleDollarSign,
      href: '/workspace/llm-usage',
      permission: 'module.llm_usage.admin',
      tileColor: 'green',
    },
    {
      // MUST stay in sync with apps/workspace/src/rail.ts — the shell renders
      // the 9-dot popover from THIS list, not from the remote's copy.
      id: 'workspace-audit-log',
      label: 'Audit Log',
      icon: ScrollText,
      href: '/workspace/audit-log',
      permission: 'view_audit',
      tileColor: 'amber',
    },
  ],
  // Object-driven — see sidebar/workspacePanels.ts.
  sidebar: workspaceSidebar,
};

// CRM remote (Wave 0 scaffold). Lives at /crm/* and is lazy-loaded via the MF
// runtime. Static metadata (id/slug/name/icon/favicon/permissions/railItems)
// is declared here so the AppSwitcher + guards render before the remote loads;
// the ONLY thing that crosses the host/remote boundary is `loader`. The railItems
// here MUST stay in sync with apps/crm/src/rail.ts. Feature modules (and their
// rail items + permission codes) are ported in a later wave.
const crmRegister: AppDefinition = {
  id: 'crm',
  slug: 'crm',
  name: 'CRM',
  shortName: 'CRM',
  icon: Contact,
  favicon: '/favicons/crm.svg',
  brandColor: '#F97316',
  // Visibility is backend-driven: CRM is COMING_SOON in the App registry, so
  // /me reports registry_status=COMING_SOON and getAccessibleApps keeps it out
  // of the switcher/launcher/routes until a platform admin flips its registry
  // status to ACTIVE. No client-side gate needed. See RUN.md "Gateway route map".
  permissions: [
    'crm.company.read',
    'crm.company.write',
    'crm.contact.read',
    'crm.contact.write',
    'crm.lead.read',
    'crm.lead.write',
    'crm.deal.read',
    'crm.deal.write',
    'crm.task.read',
    'crm.task.write',
    'crm.approval.read',
    'crm.approval.write',
    'crm.product.read',
    'crm.product.write',
    'crm.subscription.read',
    'crm.subscription.write',
    'crm.commission.read',
    'crm.commission.write',
    'crm.goal.read',
    'crm.goal.write',
    'crm.import.read',
    'crm.import.write',
    'crm.savedview.read',
    'crm.savedview.write',
    'crm.dsr.read',
    'crm.dsr.write',
    'crm.audit.read',
    'crm.user.read',
    'crm.user.write',
    'crm.report.read',
    'crm.notification.read',
    'crm.settings.read',
    'crm.settings.write',
  ] as const satisfies readonly PermissionCode[],
  loader: () => import('clarixCrm/App'),
  // Mirrors apps/crm/src/rail.ts (railItems + moreItems) EXACTLY.
  //
  // The core CRM entities (Companies / Contacts / Leads / Deals / Tasks) are
  // FLAT rail icons with no panel — there is no grouping "CRM" rail item any
  // more. Admin-defined CUSTOM objects are appended dynamically by the shell
  // (`useCrmRailItems` in SidebarPanels.tsx), so they can't be declared here.
  // The rest are panel-based (Operations / Users / Settings), each opening a
  // sub-sidebar rendered by the CRM* panel components in SidebarPanels.tsx.
  // Compliance and CRM Configuration have no rail icon of their own — they are
  // groups/rows inside the Settings panel. Keep all three in sync.
  railItems: [
    // Mirrors apps/crm/src/rail.ts — the shared AppHome surface, ungated.
    { id: 'crm-app-home', label: 'Home', icon: Home, href: '/home' },
    // Renders DashboardPage; relabelled when the shared home took "Home".
    {
      id: 'crm-home',
      label: 'Dashboard',
      icon: LayoutDashboard,
      href: '/crm',
      exact: true,
      moduleCode: 'home',
    },
    {
      id: 'crm-companies',
      moduleCode: 'companies',
      label: 'Companies',
      // "Companies" overflows the 64px rail; the icon tooltip carries the full name.
      shortLabel: 'Comp',
      icon: Building2,
      href: '/crm/companies',
      permission: 'crm.company.read',
    },
    {
      id: 'crm-contacts',
      moduleCode: 'contacts',
      label: 'Contacts',
      icon: Contact,
      href: '/crm/contacts',
      permission: 'crm.contact.read',
    },
    {
      id: 'crm-leads',
      moduleCode: 'leads',
      label: 'Leads',
      icon: Target,
      href: '/crm/leads',
      permission: 'crm.lead.read',
    },
    {
      id: 'crm-deals',
      moduleCode: 'deals',
      label: 'Deals',
      icon: Handshake,
      href: '/crm/deals',
      permission: 'crm.deal.read',
    },
    {
      id: 'crm-tasks',
      moduleCode: 'tasks',
      label: 'Tasks',
      icon: ClipboardList,
      href: '/crm/tasks',
      permission: 'crm.task.read',
    },
    // Calling (module 27). MUST stay in sync with apps/crm/src/rail.ts — the
    // SHELL renders the rail from this file, so an entry added only in the
    // remote's copy never appears. `crm.call.read` is ANY-of the three view
    // scopes, so a rep holding only view.own still gets the icon.
    {
      id: 'crm-calls',
      moduleCode: 'calling',
      label: 'Calls',
      icon: Phone,
      href: '/crm/calls',
      permission: 'crm.call.read',
    },
    // ── Custom-object rail items are injected here at runtime by the shell.
    //
    // Operations — was "Finance". Panel leads with Approvals, Product & Package
    // and Import, then a "Finance" group (Subscriptions / Renewals / Reports),
    // so the gate is ANY-of one key per row.
    {
      id: 'crm-operations',
      label: 'Operations',
      // "Operations" overflows the 64px rail. No tooltip needed — the hover
      // panel's header renders the full "Operations" title.
      shortLabel: 'Ops',
      icon: Briefcase,
      href: '/crm/operations/approvals',
      panelId: 'crm-operations',
      permission: [
        'crm.approval.read',
        'crm.product.read',
        'crm.import.read',
        'crm.subscription.read',
        'crm.report.read',
      ],
      permissionAny: true,
      matchPaths: ['/crm/operations'],
    },
    // TEMP-HIDDEN (this version): Commission rail (Commission/Incentive Structures).
    // {
    //   id: 'crm-commission',
    //   label: 'Comm',
    //   icon: Award,
    //   href: '/crm/commission',
    //   panelId: 'crm-commission',
    //   permission: 'crm.commission.read',
    // },
    // Compliance is no longer its own rail — DSR, Data Protection, Breach
    // Incidents, Data Access Logs and Consent Notices are the "Compliance" group
    // inside the Settings panel (/crm/settings/*). Audit Log and Recovery are not
    // in that panel either: both live in the 9-dot "More" popover (see moreItems).
    // Reports is no longer its own rail — it is a row in the Operations panel's
    // Finance group (/crm/operations/reports).
    // The objects & fields schema builder is no longer a rail either — the
    // "Config" rail is retired. It is the "CRM Configuration" row in the Settings
    // panel (/crm/settings/crm-configuration), where the object list is the
    // page's own tab switcher rather than panel rows.
    // Users directory — its OWN top-level rail item, promoted out of the
    // Settings panel. Its hover panel (`crm-users`, rendered shell-side as
    // CrmUsersPanel) lists All / Active / Deactivated with live counts,
    // mirroring the projects Users rail. Mirrors apps/crm/src/rail.ts.
    {
      id: 'crm-users',
      moduleCode: 'users_roles',
      label: 'Users',
      icon: Users,
      href: '/crm/users',
      panelId: 'crm-users',
      permission: 'crm.user.read',
    },
    // Settings hosts THREE groups in one panel: app settings, the Compliance
    // rows (moved off their own rail), and the master-config categories. Hence
    // the wide ANY-of gate — mirrors apps/crm/src/rail.ts. There is no
    // `crm-settings` or `crm-compliance` rail item / panelId any more.
    {
      id: 'crm-master',
      moduleCode: 'settings',
      label: 'Settings',
      icon: Settings,
      href: '/crm/settings/master-config',
      panelId: 'crm-master',
      // `crm.recovery.read` is deliberately NOT here: Recovery left the Settings
      // panel for the More popover, so it no longer opens any row in this panel.
      // `crm.audit.read` stays — it still gates the Data Access Logs row.
      permission: [
        'crm.masterconfig.read',
        'crm.settings.read',
        'crm.dsr.read',
        'crm.dataprotection.read',
        'crm.breach.read',
        'crm.consentnotices.read',
        'crm.audit.read',
      ],
      permissionAny: true,
      // Every Settings destination — app settings, CRM Configuration AND
      // compliance — shares the `/crm/settings` prefix, so one entry lights the
      // icon on all of them. Users has its own rail item.
      //
      // Audit Log and Recovery live under `/crm/settings/*` too, but they are
      // More-popover items now: the rail's "longest match wins" rule lights
      // THEIR (pinned) icon on those two paths rather than this one.
      matchPaths: ['/crm/settings'],
    },
    // Bulk data import is NOT a rail icon any more — it is an Operations panel
    // row (/crm/operations/import). Mirrors apps/crm/src/rail.ts.
  ],
  // 9-dot "More" popover — occasional, admin-only destinations that don't earn a
  // permanent rail icon but can be PINNED to the rail from the popover. Both are
  // permission-gated exactly like rail items, on the ecosystem `crm.*` aliases.
  // Mirrors apps/crm/src/rail.ts (moreItems).
  moreItems: [
    {
      id: 'crm-audit-log',
      moduleCode: 'settings',
      label: 'Audit Log',
      icon: ScrollText,
      href: '/crm/settings/audit-log',
      permission: 'crm.audit.read',
      tileColor: 'amber',
    },
    {
      id: 'crm-recovery',
      moduleCode: 'compliance',
      label: 'Recovery',
      icon: RotateCcw,
      href: '/crm/settings/recovery',
      permission: 'crm.recovery.read',
      tileColor: 'blue',
    },
  ],
  // Object-driven — see sidebar/crmPanels.ts.
  sidebar: crmSidebar,
};

const payrollRegister: AppDefinition = {
  id: 'payroll',
  slug: 'payroll',
  name: 'Payroll',
  shortName: 'PAY',
  icon: Wallet,
  favicon: '/favicons/payroll.svg',
  brandColor: '#10B981',
  // LIVE: all 12 payroll modules are ported, tools/mf/resolveRemotes.ts includes
  // `payroll` (port 5006, clarixPayroll), and the payroll-api backend is migrated
  // (served under the gateway's /payroll/* namespace). Availability is
  // backend-driven via the App registry (registry_status) like every other app.
  // Mirrors apps/payroll/src/permissions.ts (PAYROLL_PERMISSIONS). Keep both in
  // sync with the remote's permissions.ts.
  permissions: [
    'payroll.users.view',
    'payroll.roles.manage',
    'payroll.organization.view',
    'payroll.branch.view',
    'payroll.designation.view',
    'payroll.department.view',
    'payroll.statutory.view',
    'payroll.bank.view',
    'payroll.notifications.manage',
    // Payroll calculator — its own rail module (BE key:
    // module.payroll.calculator.view).
    'payroll.calculator.view',
    'payroll.pay_schedule.view',
    'payroll.salary_component.view',
    'payroll.ctc_template.view',
    'payroll.attendance.view',
    'payroll.payroll_run.view',
    'payroll.reimbursement.self',
    'payroll.loan.self',
    'payroll.payslip.self.view',
    'payroll.tds_report.view',
    'payroll.tax_form.self.view',
  ] as const satisfies readonly PermissionCode[],
  loader: () => import('clarixPayroll/App'),
  // Mirrors apps/payroll/src/rail.ts (railItems + moreItems). Keep both in sync.
  railItems: [
    // Mirrors apps/payroll/src/rail.ts — the shared AppHome surface, ungated.
    { id: 'payroll-app-home', label: 'Home', icon: Home, href: '/home' },
    // NOTE: the "Welcome" tile (`payroll-home` → `/payroll`) is gone — it opened
    // the scaffold getting-started screen, not a payroll destination. `/payroll`
    // redirects to `/payroll/home`, so opening the app from the launcher lands
    // on the Home tile above. Mirrors apps/payroll/src/rail.ts.
    {
      // Dashboard — a new payroll analytics module. Home (`/payroll`) stays the
      // getting-started welcome screen; this is the dedicated dashboard, still
      // being built, so it renders a "coming soon" placeholder for now. Ungated
      // (visible to everyone) like Home. Mirrors apps/payroll/src/rail.ts.
      id: 'payroll-dashboard',
      moduleCode: 'payroll_runs',
      label: 'Dashboard',
      icon: LayoutDashboard,
      href: '/payroll/dashboard',
    },
    {
      // Attendance has NO rail entry any more (Phase 1E of the Attendance × Run
      // Payroll merge): the run owns the import, so attendance is step 1 inside
      // the run and the old /payroll/payroll/attendance URLs redirect to the Pay
      // history list.
      //
      // The secondary panel is the prototype's "Pay history" list (Upcoming /
      // In progress / Completed) — body lives in SidebarPanels.tsx, keyed by
      // this panelId. `matchPaths` covers the whole /payroll/payroll subtree so
      // the rail stays lit inside a run cockpit.
      // Mirrors apps/payroll/src/rail.ts.
      id: 'payroll-payroll',
      moduleCode: 'payroll_runs',
      label: 'Payroll',
      icon: Wallet,
      href: '/payroll/payroll/runs',
      matchPaths: ['/payroll/payroll'],
      panelId: 'payroll-pay-history',
      permission: 'payroll.payroll_run.view',
    },
    {
      id: 'payroll-payouts',
      moduleCode: 'variable_pay',
      label: 'Variable Pay',
      icon: HandCoins,
      href: '/payroll/payouts/reimbursements',
      matchPaths: ['/payroll/payouts'],
      panelId: 'payroll-payouts',
      permission: 'payroll.reimbursement.self',
    },
    {
      // Was "Records & reports", and its href still pointed at my-payslips —
      // a page that had already moved, so clicking the rail bounced the user
      // to /payroll/profile?tab=payslips instead of into the section. It now
      // opens the section's own first page. My Payslips lives on the profile
      // and Past Payroll Runs went back to the Payroll module, so the statutory
      // tax forms are all that is left and the label says so.
      // Mirrors apps/payroll/src/rail.ts.
      id: 'payroll-compliance',
      // "Tax" is the section's real name — "Compliance" wrapped mid-word on
      // the 64px rail (see apps/payroll/src/rail.ts). No `shortLabel` needed.
      label: 'Tax',
      // FileCheck, not the old Archive storage box — that glyph described
      // "Records", not filed statutory forms.
      icon: FileCheck,
      href: '/payroll/tax/form-16',
      // Both retired prefixes stay matched so the rail is already lit while
      // their redirects resolve.
      matchPaths: ['/payroll/tax', '/payroll/compliance', '/payroll/records'],
      panelId: 'payroll-compliance',
      permission: 'payroll.tax_form.self.view',
    },
    {
      id: 'payroll-users',
      moduleCode: 'users_roles',
      label: 'Users',
      icon: Users,
      href: '/payroll/users',
      // Secondary sidebar panel (All Users / Active / Deactivated with counts)
      // — body lives in SidebarPanels.tsx, keyed by this id.
      panelId: 'payroll-users',
      permission: 'payroll.users.view',
    },
    {
      // Employee self-service — the same page as /payroll/users/:id in self
      // mode, reading `/users/me/profile`. Deliberately UNGATED (no
      // `permission`, no `moduleCode`): an employee holds none of the staff
      // keys, and the route resolves the subject from the token, so it can only
      // ever return the caller's own record. Was the avatar menu's "My payroll
      // profile". Mirrors apps/payroll/src/rail.ts.
      id: 'payroll-my-profile',
      label: 'My Profile',
      icon: IdCard,
      href: '/payroll/profile',
    },
    {
      // Roles (RBAC) and Setup & Configuration (Pay schedule / Statutory /
      // Salary components / OT-LOP / CTC templates) both live inside the
      // Settings panel now — neither has a standalone rail entry. Org profile
      // is workspace-owned, so Settings lands on Work locations (the
      // payroll-owned page under this entry).
      id: 'payroll-organization-settings',
      moduleCode: 'organization',
      label: 'Settings',
      icon: Settings,
      href: '/payroll/organization/settings/branches',
      matchPaths: ['/payroll/organization/settings', '/payroll/roles', '/payroll/setup'],
      panelId: 'payroll-settings',
      permission: 'payroll.organization.view',
    },
  ],
  // Calculator lives in the More popover now (a standalone sandbox module, NOT
  // a link out to the public /payroll/test page). Masters is intentionally
  // omitted — the page is hidden (unreachable via navigation); its route stays
  // defined in the payroll remote but is not surfaced. Keep in sync with
  // apps/payroll/src/rail.ts.
  moreItems: [
    {
      id: 'payroll-calculator',
      moduleCode: 'calculator',
      label: 'Calculator',
      icon: Calculator,
      href: '/payroll/calculator',
      permission: 'payroll.calculator.view',
      tileColor: 'blue',
    },
  ],
};

// Support remote (internal Employee Service Management). Lives at /support/* and
// is lazy-loaded via the MF runtime. Static metadata is declared here so the
// AppSwitcher + guards render before the remote loads; the ONLY thing that
// crosses the host/remote boundary is `loader`. The railItems/moreItems here
// MUST stay in sync with apps/support/src/rail.ts (per-app contract test).
// Backed by support-api (SUPPORT app, BETA on the registry) — shown when a
// workspace has the app ACTIVE.
const supportRegister: AppDefinition = {
  id: 'support',
  slug: 'support',
  name: 'Support',
  shortName: 'SD',
  icon: Headphones,
  favicon: '/favicons/support.svg',
  brandColor: '#7C3AED',
  // Mirror of apps/support/src/permissions.ts SUPPORT_PERMISSIONS — keep in sync.
  permissions: [
    'support.ticket.view.own',
    'support.ticket.view.team',
    'support.ticket.view.all',
    'support.ticket.create',
    'support.ticket.edit.own',
    'support.ticket.edit.all',
    'support.ticket.delete',
    'support.ticket.assign',
    'support.ticket.transfer',
    'support.ticket.escalate',
    'support.ticket.resolve',
    'support.ticket.cancel',
    'support.ticket.view.confidential',
    // Derived Inbox capability (server unions it into /me for agents / team
    // members / watchers) — gates the Inbox rail item below.
    'support.inbox.view',
    // Derived "My Teams" capability (server unions it into /me only for an ACTIVE
    // team member) — gates the My Teams entry in moreItems below.
    'support.team.view.own',
    'support.conversation.reply',
    'support.note.view',
    'support.note.write',
    'support.approval.decide',
    'support.masters.view',
    'support.masters.create',
    'support.masters.edit',
    'support.masters.delete',
    'support.types.view',
    'support.types.create',
    'support.types.edit',
    'support.types.delete',
    'support.teams.view',
    'support.teams.create',
    'support.teams.edit',
    'support.teams.delete',
    'support.watcher.view',
    'support.watcher.create',
    'support.watcher.edit',
    'support.watcher.delete',
    // 2026-07: support.escalation.* retired with the Escalation & Approvals tab
    // (the ladder is edited per team under support.resolution.*).
    'support.departments.view',
    'support.departments.edit',
    'support.resolution.view',
    'support.resolution.edit',
    'support.settings.view',
    'support.settings.edit',
    'support.rbac.view',
    'support.rbac.manage',
    'support.rbac.assign',
    'support.rbac.elevate',
    // Activity has its own scope keys (base = personal feed; .team / .all widen it).
    // The rail item gates on the base key — the scope keys only widen what it shows.
    'support.activity.view',
    'support.activity.view.team',
    'support.activity.view.all',
    'support.dashboard.view',
    'support.export',
  ] as const satisfies readonly PermissionCode[],
  loader: () => import('clarixSupport/App'),
  railItems: [
    // Empty landing (2026-07-23) — deliberately NOT the dashboard. `exact` so it
    // lights up only on the bare /support root, never on /support/dashboard.
    {
      id: 'support-home',
      label: 'Home',
      icon: Home,
      href: '/home',
      exact: true,
      moduleCode: 'tickets',
    },
    // The role-scoped, template-driven Dashboard — what Home WAS until 2026-07-23,
    // now its own destination. Ungated: every widget self-gates on support.* keys,
    // so the page is the union of what the viewer is (requester cards for an
    // Employee, desk analytics for an Admin) — gating the item would hide it wholesale.
    {
      id: 'support-dashboard',
      moduleCode: 'dashboard',
      label: 'Dashboard',
      icon: LayoutDashboard,
      href: '/support/dashboard',
      matchPaths: ['/support/dashboard'],
    },
    // Gated on the derived support.inbox.view (agents / team members / watchers).
    {
      id: 'support-inbox',
      label: 'Inbox',
      icon: Inbox,
      href: '/support/inbox',
      permission: 'support.inbox.view',
      moduleCode: 'tickets',
    },
    {
      id: 'support-tickets',
      moduleCode: 'tickets',
      label: 'Tickets',
      icon: Ticket,
      href: '/support/tickets',
      matchPaths: ['/support/tickets'],
      permission: [
        'support.ticket.view.own',
        'support.ticket.view.team',
        'support.ticket.view.all',
      ],
      permissionAny: true,
    },
    {
      // A first-class rail item since 2026-07 (it left the 9-dot More popover).
      // Gated on the derived support.team.view.own: only an ACTIVE team member
      // sees it (an Employee belongs to no team — the page would be empty).
      // `Network`, not a people glyph — `Users`/`UsersRound` are indistinguishable
      // at rail size and the Users item above already owns that silhouette.
      // Settings-style shell panel (SupportTeamsPanel in SidebarPanels.tsx): the
      // team list IS the sidebar, so the page renders only the reading pane.
      id: 'support-teams',
      moduleCode: 'teams',
      label: 'My Teams',
      icon: Network,
      href: '/support/teams',
      panelId: 'support-teams',
      permission: 'support.team.view.own',
    },
    {
      // Projects-app parity: the Users directory — everyone with SUPPORT access
      // and their role. An admin/RBAC surface, so it rides the rbac view key.
      // Shell panel (SupportUsersPanel in SidebarPanels.tsx): All / Active /
      // Deactivated status shortcuts, same layout as the projects Users panel.
      id: 'support-users',
      moduleCode: 'users_roles',
      label: 'Users',
      icon: UsersRound,
      href: '/support/users',
      panelId: 'support-users',
      permission: 'support.rbac.view',
    },
    {
      // Projects-app parity: Settings is a rail item with a hover/pinned shell
      // panel (SupportSettingsPanel in SidebarPanels.tsx). Visible if the user
      // can see ANY config module. Mirror of SUPPORT_CONFIG_VIEW_KEYS in
      // apps/support/src/permissions.ts. Last in the rail, above the More popover.
      id: 'support-settings',
      moduleCode: 'settings',
      label: 'Settings',
      icon: Settings,
      href: '/support/settings',
      panelId: 'support-settings',
      permission: [
        'support.masters.view',
        'support.types.view',
        'support.teams.view',
        'support.watcher.view',
        'support.resolution.view',
        'support.settings.view',
        'support.rbac.view',
      ],
      permissionAny: true,
    },
  ],
  // Activity moved into the 9-dot More popover (2026-07-23) — an occasional
  // destination, not a daily one. Still gated on support.activity.view (Supporter+),
  // so an Employee never sees it. Operations + Notifications left the nav in 2026-07
  // (routes stay live via the bell popover / dashboard links).
  moreItems: [
    {
      id: 'support-activity',
      moduleCode: 'activity',
      label: 'Activity',
      icon: Activity,
      href: '/support/activity',
      permission: 'support.activity.view',
    },
  ],
  // The first-run setup wizard is an immersive full-screen flow — the shell hides
  // both the rail and the TopBar so it doesn't read as "a page inside Support".
  chromeHiddenRoutes: ['/support/setup'],
  // Object-driven — see sidebar/supportPanels.ts.
  sidebar: supportSidebar,
};

// PMS remote (Performance Management System) — the appraisal product. Lives at
// /pms/* and is lazy-loaded via the MF runtime. Static metadata is declared here
// so the AppSwitcher and route guards render before the remote loads; the ONLY
// thing that crosses the host/remote boundary is `loader`.
const pmsRegister: AppDefinition = {
  id: 'pms',
  slug: 'pms',
  name: 'PMS',
  // 'PMS', never 'PM' — projectsRegister already owns 'PM' above, and a collision
  // makes the two indistinguishable in the launcher and the app switcher.
  shortName: 'PMS',
  icon: Gauge,
  favicon: '/favicons/pms.svg',
  // rose-900 — the SAME tone the workspace app shelf paints this app's card
  // with (`solidBg: 'bg-rose-900'` in apps/workspace/.../app-meta.ts), so the
  // switcher, the launcher, the shelf and the boot-splash ring all read as one
  // product. PMS is deliberately the deepest step of its ramp rather than the
  // -500/-600 the other apps use; the favicon's needle and hub are rose-900.
  brandColor: '#881337',
  permissions: [
    'pms.dashboard.view',
    'pms.dashboard.export',
    'pms.masters.view',
    'pms.masters.write',
    'pms.form.view',
    'pms.form.write',
    'pms.form.publish',
    'pms.run.view',
    'pms.run.write',
    'pms.evaluation.self.view',
    'pms.evaluation.self.write',
    'pms.evaluation.team.view',
    'pms.evaluation.team.write',
    'pms.comparison.view',
    'pms.comparison.self.view',
    'pms.comparison.write',
    'pms.conciliation.write',
    'pms.import.write',
    'pms.rbac.view',
    'pms.rbac.manage',
    'pms.rbac.assign',
    'pms.rbac.elevate',
    'pms.users.view',
    'pms.ai.audit.view',
  ] as const satisfies readonly PermissionCode[],
  loader: () => import('clarixPms/App'),
  // Hand-mirrors apps/pms/src/rail.ts, App-module codes included; apps/pms/src/register.test.ts
  // is the only drift guard between the two copies.
  railItems: [
    // Mirrors apps/pms/src/rail.ts — the shared AppHome surface. Ungated (a
    // greeting and starters, never data), and an ADDITIONAL route: the
    // dashboard keeps /pms as the landing screen.
    { id: 'pms-home', label: 'Home', icon: Home, href: '/home' },
    // Every role's dashboard — the one item all three personas have.
    {
      id: 'pms-dashboard',
      label: 'Dashboard',
      icon: LayoutDashboard,
      href: '/pms',
      exact: true,
      permission: 'pms.dashboard.view',
      moduleCode: 'dashboard',
    },
    {
      id: 'pms-my-evals',
      // Mirrors apps/pms/src/rail.ts. "My Evals", not "My Evaluations": the long
      // word breaks mid-word in the 64px rail, and a shortLabel would force a hover
      // tooltip — the short form as the label fits without a clip, so no tooltip.
      label: 'My Evals',
      icon: Inbox,
      href: '/pms/my-evaluations',
      permission: 'pms.evaluation.self.view',
      moduleCode: 'evaluations',
    },
    {
      id: 'pms-my-team',
      label: 'My Team',
      icon: Users,
      href: '/pms/my-team',
      // Fronts a hover panel (PmsMyTeamPanel) listing the evaluator's reportees
      // by name; clicking the icon still lands on the team queue, hovering
      // reveals the member list. The panel header title falls back to this
      // rail item's label ("My Team").
      panelId: 'pms-my-team',
      permission: 'pms.evaluation.team.view',
      moduleCode: 'evaluations',
    },
    {
      id: 'pms-forms',
      label: 'Forms',
      icon: FileEdit,
      href: '/pms/forms',
      // Fronts a hover panel (PmsFormsPanel) listing every form — clicking the
      // icon still lands on the register; hovering reveals the form list.
      panelId: 'pms-forms',
      permission: 'pms.form.view',
      moduleCode: 'forms',
    },
    {
      // The people directory — a distinct icon from My Team (which owns `Users`).
      // Uses `UsersRound` to match the Users rail item across the other apps
      // (projects, support) rather than the business-card `Contact` glyph.
      id: 'pms-users',
      label: 'Users',
      icon: UsersRound,
      href: '/pms/users',
      // Fronts a hover panel (PmsUsersPanel) grouping the directory by PMS role
      // with live counts; clicking a row deep-links the page's role filter.
      panelId: 'pms-users',
      permission: 'pms.users.view',
      moduleCode: 'users_roles',
    },
    {
      id: 'pms-settings',
      label: 'Settings',
      icon: Settings,
      href: '/pms/settings',
      panelId: 'pms-settings',
      // ANY-of: the icon fronts a panel, so it shows when at least one link
      // behind it is reachable — masters for an Admin, roles for an RBAC manager.
      permission: ['pms.masters.view', 'pms.rbac.view'],
      permissionAny: true,
      moduleCode: 'masters',
    },
  ],
};

// Calendar remote — meetings, agenda, booking pages and rooms.
// Lives at /calendar/* and is lazy-loaded via the MF runtime; only `loader`
// crosses the host/remote boundary.
const calendarRegister: AppDefinition = {
  id: 'calendar',
  slug: 'calendar',
  name: 'Calendar',
  shortName: 'CAL',
  icon: Calendar,
  favicon: '/favicons/calendar.svg',
  // cyan-600 — the favicon's stroke colour; the index.html boot ring must match it.
  brandColor: '#0891B2',
  description: 'Meetings, agenda, booking pages and rooms.',
  // Mirrors apps/calendar/src/permissions.ts (FE/BE string parity with calendar-api).
  permissions: [
    'calendar.meetings.view',
    'calendar.meetings.create',
    'calendar.meetings.edit',
    'calendar.meetings.cancel',
    'calendar.meetings.delete',
    'calendar.meetings.execute',
    'calendar.meetings.end',
    'calendar.meetings.extend',
    'calendar.meetings.timesheet',
    'calendar.meetings.view_mom',
    'calendar.meetings.clara_insights',
    'calendar.meetings.analytics',
    'calendar.meetings.complete_task',
    'calendar.agenda.view',
    'calendar.recordings.create',
    'calendar.recordings.share',
    'calendar.recordings.erase',
    'calendar.availability.share',
    'calendar.team_availability.view',
    'calendar.team_availability.details',
    'calendar.booking.view',
    'calendar.booking.create',
    'calendar.booking.edit',
    'calendar.booking.delete',
    'calendar.booking.manage_all',
    'calendar.integrations.connect',
    'calendar.rooms.view',
    'calendar.rooms.book',
    'calendar.rooms.manage',
    'calendar.calendars.manage',
    'calendar.notification_settings.manage',
    'calendar.integrations.manage',
    'calendar.integrations.view',
    'calendar.integrations.revoke',
    'calendar.roles.view',
    'calendar.roles.manage',
    'calendar.roles.assign',
    'calendar.roles.elevate',
    'calendar.audit.view',
  ] as const satisfies readonly PermissionCode[],
  loader: () => import('clarixCalendar/App'),
  // Hand-mirrors apps/calendar/src/rail.ts (railItems + moreItems), gates and App-module codes
  // included; the gates resolve through the `calendar` case in sidebar/rail-permissions.ts.
  railItems: [
    { id: 'calendar-app-home', label: 'Home', icon: Home, href: '/home' },
    {
      id: 'calendar-calendar',
      label: 'Calendar',
      icon: Calendar,
      href: '/calendar',
      exact: true,
      matchPaths: ['/calendar/analytics'],
      panelId: 'calendar-main',
      permission: CALENDAR_SHELL_PERM.MEETINGS_VIEW,
      moduleCode: 'calendar',
    },
    {
      id: 'calendar-agenda',
      label: 'Agenda',
      icon: CalendarClock,
      href: '/calendar/agenda',
      permission: CALENDAR_SHELL_PERM.AGENDA_VIEW,
      moduleCode: 'calendar',
    },
    {
      id: 'calendar-booking',
      label: 'Booking',
      icon: Link2,
      href: '/calendar/booking',
      permission: CALENDAR_SHELL_PERM.BOOKING_VIEW,
      moduleCode: 'booking',
    },
    {
      id: 'calendar-rooms',
      label: 'Rooms',
      icon: DoorOpen,
      href: '/calendar/rooms',
      permission: CALENDAR_SHELL_PERM.ROOMS_VIEW,
      moduleCode: 'rooms',
    },
    {
      // Any-of: a recorder (recordings.create) or a share grantee (view_mom). These keys also
      // feed the shell's Quick Meeting gate through the rail's permission query (§18.10).
      id: 'calendar-recordings',
      label: 'Recordings',
      shortLabel: 'Recs',
      icon: Mic,
      href: '/calendar/recordings',
      permission: [CALENDAR_SHELL_PERM.RECORDINGS_CREATE, CALENDAR_SHELL_PERM.MEETINGS_VIEW_MOM],
      permissionAny: true,
      // Core, not `recordings`: old recordings stay reachable with that module off (mirrors rail.ts).
      moduleCode: 'calendar',
    },
    {
      id: 'calendar-settings',
      label: 'Settings',
      icon: Settings,
      href: '/calendar/settings/preferences',
      matchPaths: ['/calendar/settings'],
      panelId: 'calendar-settings',
      moduleCode: 'settings',
    },
  ],
  moreItems: [
    {
      id: 'calendar-analytics',
      label: 'Analytics',
      icon: BarChart2,
      href: '/calendar/analytics',
      tileColor: 'purple',
      permission: CALENDAR_SHELL_PERM.MEETINGS_ANALYTICS,
      moduleCode: 'analytics',
    },
    {
      id: 'calendar-calendars',
      label: 'Calendars',
      icon: Layers,
      href: '/calendar/settings/calendars',
      tileColor: 'green',
      permission: CALENDAR_SHELL_PERM.MEETINGS_VIEW,
      moduleCode: 'settings',
    },
    {
      id: 'calendar-audit-log',
      label: 'Audit log',
      icon: FileText,
      href: '/calendar/settings/audit',
      tileColor: 'amber',
      permission: CALENDAR_SHELL_PERM.AUDIT_VIEW,
      moduleCode: 'users_roles',
    },
  ],
  // Object-driven — see sidebar/calendarPanels.ts.
  sidebar: calendarSidebar,
};

// HRMS remote — people, attendance, leave, overtime and holidays. Lives at /hrms/*;
// only `loader` crosses the host/remote boundary. Visibility is backend-driven
// (/me's active_workspace_apps['HRMS'] + registry_status), like every other app.
const hrmsRegister: AppDefinition = {
  id: 'hrms',
  slug: 'hrms',
  name: 'HRMS',
  shortName: 'HR',
  icon: Users,
  favicon: '/favicons/hrms.svg',
  // pink-600 — the favicon's stroke colour; the index.html boot ring must match it.
  brandColor: '#DB2777',
  description: 'People, attendance, leave, overtime and holidays.',
  // Mirrors apps/hrms/src/permissions.ts (FE/BE string parity with hrms-api).
  permissions: [
    'hrms.dashboard.view',
    'hrms.attendance.view.own',
    'hrms.attendance.request.create',
    'hrms.leave.view.own',
    'hrms.leave.apply',
    'hrms.overtime.view.own',
    'hrms.overtime.claim',
    'hrms.holiday.view',
    'hrms.profile.view.own',
    'hrms.profile.edit.own',
    'hrms.approval.view',
    'hrms.approval.decide.team',
    'hrms.approval.decide.all',
    'hrms.attendance.view.team',
    'hrms.leave.view.team',
    'hrms.leave.view.private',
    'hrms.leave.apply.onbehalf',
    'hrms.overtime.view.team',
    'hrms.people.view',
    'hrms.people.manage',
    'hrms.prejoining.view',
    'hrms.prejoining.manage',
    'hrms.prejoining.documents.view',
    'hrms.onboarding.templates.manage',
    'hrms.attendance.view.all',
    'hrms.attendance.verify',
    'hrms.attendance.policy.manage',
    'hrms.attendance.policy.backdate',
    'hrms.attendance.issue.exception',
    'hrms.attendance.correct.others',
    'hrms.attendance.roster.manage',
    'hrms.attendance.period.reopen',
    'hrms.leave.view.all',
    'hrms.leave.setup.manage',
    'hrms.leave.edit',
    'hrms.overtime.view.all',
    'hrms.overtime.policy.manage',
    'hrms.overtime.edit',
    'hrms.holiday.manage',
    'hrms.report.view',
    'hrms.settings.manage',
    'hrms.workspace.manage',
    'hrms.rbac.view',
    'hrms.rbac.manage',
  ] as const satisfies readonly PermissionCode[],
  loader: () => import('clarixHrms/App'),
  // Hand-mirrors apps/hrms/src/rail.ts; apps/hrms/src/register.test.ts guards the remote's copy.
  // Gates resolve through the `hrms` case in sidebar/rail-permissions.ts.
  railItems: [
    { id: 'hrms-home', label: 'Home', icon: Home, href: '/home' },
    // The HRMS landing (/hrms redirects here); no panel.
    {
      id: 'hrms-dashboard',
      label: 'Dashboard',
      icon: LayoutDashboard,
      href: '/hrms/dashboard',
      permission: HRMS_SHELL_PERM.DASHBOARD_VIEW,
      moduleCode: 'dashboard',
    },
    {
      id: 'hrms-inbox',
      label: 'Inbox',
      icon: Inbox,
      href: '/hrms/inbox',
      permission: HRMS_SHELL_PERM.APPROVAL_VIEW,
      moduleCode: 'approvals',
      // The remote publishes the pending count under this key (apps/hrms/src/rail.ts).
      badgeKey: 'hrms.inbox',
    },
    {
      id: 'hrms-people',
      label: 'People',
      icon: Users,
      href: '/hrms/people',
      panelId: 'people',
      permission: HRMS_SHELL_PERM.PEOPLE_VIEW,
      moduleCode: 'people',
    },
    {
      id: 'hrms-attendance',
      label: 'Attendance',
      icon: UserCheck,
      href: '/hrms/attendance',
      // Not 'attendance': panel ids are global across apps.
      panelId: 'hrms-attendance',
      // Own or team: every attendance HR key implies view.team, so HR without view.own still gets in.
      permission: [HRMS_SHELL_PERM.ATTENDANCE_VIEW_OWN, HRMS_SHELL_PERM.ATTENDANCE_VIEW_TEAM],
      permissionAny: true,
      moduleCode: 'attendance',
    },
    {
      id: 'hrms-leave',
      label: 'Leave',
      icon: CalendarMinus2,
      href: '/hrms/leave',
      // Not 'leave': panel ids are global across apps.
      panelId: 'hrms-leave',
      permission: HRMS_SHELL_PERM.LEAVE_VIEW_OWN,
      moduleCode: 'leave',
    },
    {
      id: 'hrms-overtime',
      label: 'Overtime',
      icon: Timer,
      href: '/hrms/overtime',
      // Not 'overtime': panel ids are global across apps.
      panelId: 'hrms-overtime',
      permission: HRMS_SHELL_PERM.OVERTIME_VIEW_OWN,
      moduleCode: 'overtime',
    },
    {
      id: 'hrms-holidays',
      label: 'Holidays',
      icon: CalendarDays,
      href: '/hrms/holidays',
      permission: HRMS_SHELL_PERM.HOLIDAY_VIEW,
      moduleCode: 'holidays',
    },
    {
      id: 'hrms-profile',
      label: 'My Profile',
      icon: IdCard,
      href: '/hrms/profile',
      permission: HRMS_SHELL_PERM.PROFILE_VIEW_OWN,
      moduleCode: 'people',
    },
    {
      id: 'hrms-settings',
      label: 'Settings',
      icon: Settings,
      href: '/hrms/settings',
      // Not 'settings': panel ids are global across apps and projects already owns that one.
      panelId: 'hrms-settings',
      permission: [
        HRMS_SHELL_PERM.SETTINGS_MANAGE,
        HRMS_SHELL_PERM.ATTENDANCE_POLICY_MANAGE,
        HRMS_SHELL_PERM.LEAVE_SETUP_MANAGE,
        HRMS_SHELL_PERM.OVERTIME_POLICY_MANAGE,
        // HRMS_SHELL_PERM.ONBOARDING_TEMPLATES_MANAGE, // Onboarding hidden for now (FE only).
      ],
      permissionAny: true,
      moduleCode: 'settings',
    },
  ],
  // Hand-mirrors apps/hrms/src/rail.ts `moreItems`: the 9-dot popover renders from this list.
  moreItems: [
    {
      id: 'hrms-audit-log',
      label: 'Audit Log',
      icon: ScrollText,
      href: '/hrms/audit-log',
      tileColor: 'amber',
      permission: HRMS_SHELL_PERM.RBAC_VIEW,
      moduleCode: 'users_roles',
    },
  ],
  // Object-driven — see sidebar/hrmsPanels.ts.
  sidebar: hrmsSidebar,
};

// Order matters: drives the sidebar/switcher order and the default landing app
// (index 0). The Workspace app lives at /workspace/* — the remote renders
// workspace-admin OR platform-admin (Sarvadhi staff) sections conditionally
// based on the JWT's workspace_role claim.
export const APP_REGISTRY: readonly AppDefinition[] = [
  projectsRegister,
  supportRegister,
  workspaceRegister,
  // CRM is registered in all environments. The router maps APP_REGISTRY to the
  // `/<slug>/*` routes, so including crmRegister here mounts BOTH its launcher
  // entry AND its /crm/* route. Per-workspace + per-user enablement still gates
  // visibility via /me's active_workspace_apps (see getAccessibleApps).
  crmRegister,
  payrollRegister,
  pmsRegister,
  calendarRegister,
  hrmsRegister,
];
